import React from 'react';
import ChevronDownIcon from 'app/components/TimelineCard/ChevronDownIcon';
import BlackCheckMarkIcon from 'app/assets/svgIcons/BlackCheckMarkIcon';
import styles from '../SlideOutPanel.module.scss';
import { FeatureRow } from './Features';

export type FeatureSortOption =
  | 'aToZ'
  | 'mvp'
  | 'gaDate'
  | 'publicBetaDate'
  | 'custom';

interface SortOptionConfig {
  value: FeatureSortOption;
  label: string;
  testId: string;
  /** When true, row is disabled until user has reordered via drag/drop or keyboard */
  lockedUntilReorder?: boolean;
}

const SORT_OPTIONS: SortOptionConfig[] = [
  { value: 'aToZ', label: 'A-Z', testId: 'features-sort-option-az' },
  { value: 'mvp', label: 'MVP', testId: 'features-sort-option-mvp' },
  { value: 'gaDate', label: 'GA Date', testId: 'features-sort-option-gadate' },
  {
    value: 'publicBetaDate',
    label: 'Public Beta Date',
    testId: 'features-sort-option-publicbetadate'
  },
  {
    value: 'custom',
    label: 'Custom',
    testId: 'features-sort-option-custom',
    lockedUntilReorder: true
  }
];

export type FeatureSortSelection = FeatureSortOption | null;

interface FeaturesSortDropdownProps {
  value: FeatureSortSelection;
  onChange: (sortBy: FeatureSortOption) => void;
  /** When false, the Custom option cannot be selected */
  isCustomEnabled?: boolean;
  disabled?: boolean;
}

export const FeaturesSortDropdown: React.FC<FeaturesSortDropdownProps> = ({
  value,
  onChange,
  isCustomEnabled = false,
  disabled = false
}) => {
  const [showDropdown, setShowDropdown] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement | null>(null);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    if (!showDropdown) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  const handleOptionClick = (
    option: FeatureSortOption,
    itemDisabled: boolean
  ) => {
    if (itemDisabled) return;
    onChange(option);
    setShowDropdown(false);
  };

  return (
    <div ref={dropdownRef} style={{ position: 'relative', marginRight: '1px' }}>
      <button
        className={styles.sortPicker}
        onClick={e => {
          e.stopPropagation();
          if (!disabled) {
            setShowDropdown(prev => !prev);
          }
        }}
        disabled={disabled}
        data-testid="features-sort-picker"
      >
        <span className={styles.sortLabel}>Sort By</span>
        <ChevronDownIcon className="chevronDownIcon" />
      </button>
      {showDropdown && (
        <div
          className={styles.sortDropdownMenu}
          data-testid="features-sort-dropdown"
          role="menu"
        >
          {SORT_OPTIONS.map(option => {
            const itemDisabled =
              option.lockedUntilReorder === true && !isCustomEnabled;
            return (
              <div
                key={option.value}
                className={
                  itemDisabled
                    ? styles.sortDropdownItemDisabled
                    : styles.sortDropdownItem
                }
                onClick={e => {
                  e.stopPropagation();
                  handleOptionClick(option.value, itemDisabled);
                }}
                data-testid={option.testId}
                role="menuitem"
                aria-disabled={itemDisabled}
              >
                <span>{option.label}</span>
                {value === option.value && (
                  <BlackCheckMarkIcon
                    width={18}
                    height={16}
                    className={styles.sortCheckmark}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Helper function to parse GA date for sorting
// Returns a timestamp with encoded granularity for proper precedence:
// - Quarters (least specific) sort first: timestamp + 0ms
// - Months (medium specific) sort second: timestamp + 1ms
// - Exact dates (most specific) sort third: timestamp + 2ms
const parseGADateForSort = (gaDate: string | undefined): number => {
  // Handle empty, undefined, null, N/A, or TBD
  if (!gaDate || gaDate === '' || gaDate === 'N/A' || gaDate === 'TBD') {
    return Infinity; // Put N/A, TBD, and empty at the end
  }

  const trimmedDate = gaDate.trim();
  if (trimmedDate === '') {
    return Infinity;
  }

  // Granularity offsets (in milliseconds)
  const GRANULARITY_OFFSET = {
    QUARTER: 0, // Broadest - comes first
    MONTH: 1, // Medium - comes second
    DATE: 2 // Most specific - comes third
  };

  // Handle ISO date format (YYYY-MM-DD) - Most specific
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmedDate)) {
    return new Date(trimmedDate).getTime() + GRANULARITY_OFFSET.DATE;
  }

  // Handle quarter format (Q1 2025, Q2 2024, etc.)
  // Fiscal year quarters: Q1=Dec-Feb, Q2=Mar-May, Q3=Jun-Aug, Q4=Sep-Nov
  const quarterMatch = trimmedDate.match(/^Q([1-4])\s+(\d{4})$/i);
  if (quarterMatch) {
    const quarter = parseInt(quarterMatch[1], 10);
    const year = parseInt(quarterMatch[2], 10);

    // Map quarters to their starting months (fiscal year starting December)
    // Q1 2025: Dec 2024, Jan 2025, Feb 2025 → Start: Dec 2024 (month 11, year 2024)
    // Q2 2025: Mar 2025, Apr 2025, May 2025 → Start: Mar 2025 (month 2, year 2025)
    // Q3 2025: Jun 2025, Jul 2025, Aug 2025 → Start: Jun 2025 (month 5, year 2025)
    // Q4 2025: Sep 2025, Oct 2025, Nov 2025 → Start: Sep 2025 (month 8, year 2025)
    let month: number;
    let actualYear: number;

    if (quarter === 1) {
      // Q1 starts in December of the previous year
      month = 11; // December
      actualYear = year - 1;
    } else if (quarter === 2) {
      // Q2 starts in March
      month = 2; // March
      actualYear = year;
    } else if (quarter === 3) {
      // Q3 starts in June
      month = 5; // June
      actualYear = year;
    } else {
      // Q4 starts in September
      month = 8; // September
      actualYear = year;
    }

    // Quarters are least specific (broadest), so they get +0ms offset
    // This ensures Q2 2025 appears before "Mar 2025" and "2025-03-01"
    return (
      new Date(actualYear, month, 1).getTime() + GRANULARITY_OFFSET.QUARTER
    );
  }

  // Handle month format (Sep 2025, Jan 2024, etc.) - Medium specificity
  const monthMatch = trimmedDate.match(/^([A-Za-z]{3})\s+(\d{4})$/);
  if (monthMatch) {
    const monthStr = monthMatch[1];
    const year = parseInt(monthMatch[2], 10);
    const monthMap: { [key: string]: number } = {
      jan: 0,
      feb: 1,
      mar: 2,
      apr: 3,
      may: 4,
      jun: 5,
      jul: 6,
      aug: 7,
      sep: 8,
      oct: 9,
      nov: 10,
      dec: 11
    };
    const month = monthMap[monthStr.toLowerCase()];
    if (month !== undefined) {
      // Months get +1ms offset to sort after quarters but before exact dates
      return new Date(year, month, 1).getTime() + GRANULARITY_OFFSET.MONTH;
    }
  }

  // Handle month without year (Sep, Jan, etc.) - assume current year - Medium specificity
  const monthOnlyMatch = trimmedDate.match(/^([A-Za-z]{3})$/i);
  if (monthOnlyMatch) {
    const monthStr = monthOnlyMatch[1];
    const currentYear = new Date().getFullYear();
    const monthMap: { [key: string]: number } = {
      jan: 0,
      feb: 1,
      mar: 2,
      apr: 3,
      may: 4,
      jun: 5,
      jul: 6,
      aug: 7,
      sep: 8,
      oct: 9,
      nov: 10,
      dec: 11
    };
    const month = monthMap[monthStr.toLowerCase()];
    if (month !== undefined) {
      // Months get +1ms offset to sort after quarters but before exact dates
      return (
        new Date(currentYear, month, 1).getTime() + GRANULARITY_OFFSET.MONTH
      );
    }
  }

  return Infinity; // Unrecognized format goes to the end
};

/**
 * Sort features based on the selected sort option.
 *
 * @param features - Caller's `localFeatures` state (not the raw API `rows` prop).
 *   Drag-and-drop updates that state, so `custom` and `null` must return it as-is
 *   to preserve manual order.
 * @param sortBy - Sort option; `null` = no sort (current list order in state).
 */
export const sortFeaturesByOption = (
  features: FeatureRow[],
  sortBy: FeatureSortSelection
): FeatureRow[] => {
  if (sortBy === null || sortBy === 'custom') {
    return features;
  }

  const sorted = [...features];

  if (sortBy === 'aToZ') {
    sorted.sort((a, b) =>
      a.name.localeCompare(b.name, undefined, {
        sensitivity: 'base',
        numeric: true
      })
    );
  } else if (sortBy === 'mvp') {
    // Sort by MVP: MVP features first (checked = true), then others
    sorted.sort((a, b) => {
      if (a.mvp === b.mvp) return 0;
      return a.mvp ? -1 : 1; // MVP features come first
    });
  } else if (sortBy === 'gaDate') {
    // Sort by GA Date: earliest dates first, N/A and TBD at the end
    sorted.sort((a, b) => {
      const dateA = parseGADateForSort(a.ga);
      const dateB = parseGADateForSort(b.ga);

      return dateA - dateB;
    });
  } else if (sortBy === 'publicBetaDate') {
    // Sort by Public Beta Date: earliest dates first, N/A and TBD at the end
    sorted.sort((a, b) => {
      const dateA = parseGADateForSort(a.publicBeta);
      const dateB = parseGADateForSort(b.publicBeta);

      return dateA - dateB;
    });
  }

  return sorted;
};

export default FeaturesSortDropdown;
